import type { ReactNode } from "react";
import { cn } from "./cn.ts";

const control = cn(
  "w-full rounded-xl border border-input bg-card px-3.5 text-lg text-card-foreground",
  "placeholder:text-muted-foreground focus-visible:border-ring focus-visible:outline-2",
  "focus-visible:outline-offset-0 focus-visible:outline-ring disabled:opacity-60",
  "aria-[invalid=true]:border-danger",
);

export const inputClass = cn(control, "min-h-11 py-2");
export const textareaClass = cn(control, "min-h-28 py-2.5 leading-relaxed");
export const selectClass = cn(control, "min-h-11 py-2 pr-9");

interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  /**
   * Id of the control; hint and error get `${htmlFor}-hint` / `${htmlFor}-error`
   * for aria-describedby.
   */
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}

export const Field = ({
  label,
  hint,
  error,
  htmlFor,
  className,
  children,
}: FieldProps): ReactNode => (
  <div className={cn("flex flex-col gap-1.5", className)}>
    <label htmlFor={htmlFor} className="font-semibold">
      {label}
    </label>
    {children}
    {hint && (
      <p id={htmlFor ? `${htmlFor}-hint` : undefined} className="text-muted-foreground">
        {hint}
      </p>
    )}
    {error && (
      <p id={htmlFor ? `${htmlFor}-error` : undefined} className="font-medium text-danger">
        {error}
      </p>
    )}
  </div>
);
